import { Combobox, Icon, Menu, Text, useFilter } from "@ttc3k/trekker";
import { Plus, Trash } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Box, Flex, styled } from "styled-system/jsx";

export type TagMatch = "any" | "all";

export type TagCondition = {
  tagIds: string[];
  include: boolean;
  match: TagMatch;
};

export type TagFilterOption = {
  _id: string;
  name: string;
};

export type TagFilterBuilderProps = {
  tagOptions: TagFilterOption[];
  value: TagCondition[];
  onChange: (value: TagCondition[]) => void;
  /** The noun used in the inline sentence, e.g. "listings". Defaults to the generic translation. */
  subjectLabel?: string;
};

const InlineTrigger = styled("button", {
  base: {
    display: "inline-flex",
    alignItems: "center",
    color: "text.accent.blue.dark",
    fontWeight: "semibold",
    fontSize: "sm",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    cursor: "pointer",
    _hover: { opacity: 0.8 },
  },
});

const IconButton = styled("button", {
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    p: "100",
    borderRadius: "100",
    color: "icon.mid",
    cursor: "pointer",
    _hover: { color: "text.error.dark", bg: "bg.lightest" },
  },
});

const AddConditionButton = styled("button", {
  base: {
    display: "inline-flex",
    alignItems: "center",
    gap: "50",
    w: "fit-content",
    color: "text.accent.blue.dark",
    fontWeight: "semibold",
    fontSize: "sm",
    cursor: "pointer",
    _hover: { opacity: 0.8 },
  },
});

function InlineChoice({
  label,
  options,
  onSelect,
}: {
  label: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
}) {
  return (
    <Menu.Root
      trigger={<InlineTrigger type="button">{label}</InlineTrigger>}
      positioning={{ placement: "bottom-start" }}
      _positionerProps={{ zIndex: "modal" }}
      onSelect={(d: { value: string }) => onSelect(d.value)}
    >
      {options.map((opt) => (
        <Menu.Item key={opt.value} value={opt.value}>
          {opt.label}
        </Menu.Item>
      ))}
    </Menu.Root>
  );
}

function TagConditionRow({
  condition,
  tagOptions,
  subjectLabel,
  onUpdate,
  onRemove,
}: {
  condition: TagCondition;
  tagOptions: TagFilterOption[];
  subjectLabel: string;
  onUpdate: (update: Partial<TagCondition>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { contains } = useFilter({ sensitivity: "base" });

  const { collection, filter } = Combobox.useCollection({
    initialItems: tagOptions.map((opt) => ({ value: opt._id, label: opt.name })),
    filter: contains,
  });

  return (
    <Flex
      flexDir="column"
      gap="150"
      p="250"
      border="1px solid"
      borderColor="border.light"
      borderRadius="100"
    >
      <Flex gap="100" alignItems="flex-start" w="full">
        <Box flex="1" minW={0}>
          <Combobox.Root
            multiple
            value={condition.tagIds}
            collection={collection}
            onValueChange={(d: { value: string[] }) =>
              onUpdate({ tagIds: d.value })
            }
            onInputValueChange={(d: { inputValue: string }) =>
              filter(d.inputValue)
            }
            placeholder={t("core:FILTER_BUILDER.TAG_PLACEHOLDER")}
            tagVisualVariant="accent.blue"
            size="md"
            css={{
              "& [data-scope='combobox'][data-part='input'], & [data-scope='combobox'][data-part='input-wrapper']":
                {
                  bg: "white",
                },
            }}
          >
            {collection.items.map((item) => (
              <Combobox.Item key={item.value} item={item} label={item.label} />
            ))}
          </Combobox.Root>
        </Box>
        <IconButton
          type="button"
          aria-label={t("core:FILTER_BUILDER.REMOVE_CONDITION")}
          onClick={onRemove}
        >
          <Icon Element={Trash} size="sm" />
        </IconButton>
      </Flex>

      <Flex flexWrap="wrap" alignItems="center" gap="50">
        <InlineChoice
          label={
            condition.include
              ? t("core:FILTER_BUILDER.INCLUDE")
              : t("core:FILTER_BUILDER.EXCLUDE")
          }
          options={[
            { value: "include", label: t("core:FILTER_BUILDER.INCLUDE") },
            { value: "exclude", label: t("core:FILTER_BUILDER.EXCLUDE") },
          ]}
          onSelect={(v) => onUpdate({ include: v === "include" })}
        />
        <Text visual="smallRegular" color="text.mid">
          {t("core:FILTER_BUILDER.SENTENCE_SUBJECT", { subject: subjectLabel })}
        </Text>
        <InlineChoice
          label={
            condition.match === "all"
              ? t("core:FILTER_BUILDER.MATCH_ALL")
              : t("core:FILTER_BUILDER.MATCH_ANY")
          }
          options={[
            { value: "any", label: t("core:FILTER_BUILDER.MATCH_ANY") },
            { value: "all", label: t("core:FILTER_BUILDER.MATCH_ALL") },
          ]}
          onSelect={(v) => onUpdate({ match: v as TagMatch })}
        />
        <Text visual="smallRegular" color="text.mid">
          {t("core:FILTER_BUILDER.SENTENCE_TAIL")}
        </Text>
      </Flex>
    </Flex>
  );
}

export const TagFilterBuilder = ({
  tagOptions,
  value,
  onChange,
  subjectLabel,
}: TagFilterBuilderProps) => {
  const { t } = useTranslation();
  const subject = subjectLabel ?? t("core:FILTER_BUILDER.SUBJECT_DEFAULT");

  const handleUpdate = (index: number, update: Partial<TagCondition>) => {
    onChange(value.map((c, i) => (i === index ? { ...c, ...update } : c)));
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([...value, { tagIds: [], include: true, match: "any" }]);
  };

  return (
    <Flex flexDir="column" gap="200">
      {value.map((condition, index) => (
        <TagConditionRow
          key={index}
          condition={condition}
          tagOptions={tagOptions}
          subjectLabel={subject}
          onUpdate={(update) => handleUpdate(index, update)}
          onRemove={() => handleRemove(index)}
        />
      ))}
      {tagOptions.length > 0 && (
        <AddConditionButton type="button" onClick={handleAdd}>
          <Icon Element={Plus} size="sm" />
          {t("core:FILTER_BUILDER.ADD_CONDITION")}
        </AddConditionButton>
      )}
    </Flex>
  );
};
